/**
 * useRefreshUserProfile Hook
 *
 * Centralized hook for refreshing user profile data after mutations.
 * Fetches latest user data from backend and updates both React Query cache and Zustand auth store.
 *
 * Used by:
 * - useUploadPhoto
 * - useDeletePhoto
 * - useSetCoverPhoto
 * - Any other profile mutation hooks
 */

import { useQueryClient } from "@tanstack/react-query"
import { useAuthStore } from "@/features/auth/store/authStore"
import * as userService from "@/features/profile/services/user.service"

export function useRefreshUserProfile() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore(state => state.setUser)

  /**
   * Refresh user profile data from backend
   * Updates both React Query cache and Zustand auth store
   */
  const refreshUserProfile = async () => {
    try {
      // Fetch latest user data from backend
      const { user } = await userService.getProfile()

      // Update React Query cache
      queryClient.setQueryData(["user"], user)

      // Update Zustand auth store with latest data
      setUser(user)

      return user
    } catch (error) {
      console.error("Failed to refresh user profile:", error)
      throw error
    }
  }

  return { refreshUserProfile }
}
